import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// Assets
import addIcon from "../../assets/add_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png"; // You may need to add this

// Components
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import CustomButton from "../../components/CustomButton";
import DashboardSection from "../../components/DasboardSection";
import Card from "../../components/Card";
import InformationBox from "../../components/InformationBox"; // Import this component
import {
  deletePaperColor,
  updatePaperColor,
} from "../../services/paper/storehouseServices";
import AreYouSureModal from "../../components/AreYouSureModal";
import { set } from "date-fns";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import Loader from "../../components/Loader";

const PaperDataEntryModal = ({
  currentGroupCode,
  setCurrentGroupCode,
  setCurrentGroupDesign,
  currentGroupDesign,
  colorInputs,
  setColorInputs,
  isEditMode,
  errorFields,
  setIsEditing,
  getData,
}) => {
  const { t } = useTranslation("general");

  // State to track which roll is being edited
  const [editingRollIndex, setEditingRollIndex] = useState(null);
  const [tempRollData, setTempRollData] = useState(null);

  // general states
  const [showSureModal, setShowSureModal] = useState(false);
  const [updateLoader, setUpdateLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [showMaxColorsWarning, setShowMaxColorsWarning] = useState(false);

  // Count how many new colors (without IDs) are being added
  const newColorsCount = colorInputs.filter((color) => !color.id).length;

  const handleAddColorField = () => {
    // Check if already 5 NEW colors exist (colors without IDs)
    if (newColorsCount >= 5) {
      setShowMaxColorsWarning(true);
      return;
    }

    setColorInputs([
      ...colorInputs,
      {
        colorCode: "",
        colorName: "",
        dimension: "",
        paperThickness: "",
        availableRollsInStock: 0,
        availableStock: 0,
        notes: "",
        createdAt: new Date(),
        isNew: true,
      },
    ]);
  };

  const handleColorInputChange = (index, field, value) => {
    if (editingRollIndex === index && tempRollData) {
      setTempRollData({ ...tempRollData, [field]: value });
    } else {
      const updated = [...colorInputs];
      updated[index][field] = value;
      setColorInputs(updated);
    }
  };

  const handleEnter = (e, index) => {
    if (
      e.key === "Enter" &&
      index === colorInputs.length - 1 &&
      newColorsCount < 5
    ) {
      e.preventDefault();
      handleAddColorField();
    }
  };

  // Start editing a specific roll
  const handleEditRoll = (index) => {
    setEditingRollIndex(index);
    setIsEditing(true);
    setTempRollData({ ...colorInputs[index] });
  };

  // Save changes to a specific roll
  const handleSaveRoll = async (index) => {
    try {
      setUpdateLoader(true);
      const rollId = colorInputs[index]?.id;

      if (rollId) {
        const payload = {
          ...tempRollData,
          dimension: parseFloat(tempRollData.dimension),
          paperThickness: parseFloat(tempRollData.paperThickness),
          availableRollsInStock: parseInt(tempRollData.availableRollsInStock),
          availableStock: parseFloat(tempRollData.availableStock),
        };

        const response = await updatePaperColor(rollId, payload);

        if (response.status === 200) {
          // Refresh data from parent component
          getData();
        }
      }

      // Update local state
      const updated = [...colorInputs];
      updated[index] = tempRollData;
      setColorInputs(updated);

      // Reset editing state
      setEditingRollIndex(null);
      setTempRollData(null);
    } catch (error) {
      console.error("Error saving roll:", error);
    } finally {
      setUpdateLoader(false);
      setIsEditing(false);
    }
  };

  // Cancel editing a specific roll
  const handleCancelEdit = () => {
    setEditingRollIndex(null);
    setTempRollData(null);
    setIsEditing(false);
  };

  // Delete a specific roll
  const deleteRoll = async (index) => {
    const rollId = colorInputs[index].id;

    if (rollId) {
      try {
        setDeleteLoader(true);
        const response = await deletePaperColor(rollId);

        // Remove from local state
        if (response.status === 200) {
          const updated = colorInputs.filter((_, i) => i !== index);
          setColorInputs(updated);

          getData();
        }
      } catch (error) {
        console.error("Error deleting roll:", error);
      } finally {
        setDeleteLoader(false);
      }
    } else {
      // Just remove from local state if no ID (new roll not saved yet)
      const updated = colorInputs.filter((_, i) => i !== index);
      setColorInputs(updated);
    }

    // Reset editing state if this roll was being edited
    if (editingRollIndex === index) {
      setEditingRollIndex(null);
      setTempRollData(null);
    }
  };

  // Render roll in view mode (for edit mode)
  const renderViewMode = (color, index) => {
    return (
      <CollapsibleCard
        collapsed={true}
        title={`${color.colorCode}`}
        body={
          <>
            <div className="o-column o-column--gap-size_300 u-background-color_gray-light u-border-radius_8">
              <div className="o-column o-column--gap-size_200">
                <div className="o-row o-row--gap-size_200">
                  <span className="t-bold_semi-bold u-width--min-width_100">
                    {t("categoryName")}:
                  </span>
                  <span>{color.colorCode || "-"}</span>
                </div>
                <div className="o-row o-row--gap-size_200">
                  <span className="t-bold_semi-bold u-width--min-width_100">
                    {t("rollCode")}:
                  </span>
                  <span>{color.colorName || "-"}</span>
                </div>
                <div className="o-row o-row--gap-size_200">
                  <span className="t-bold_semi-bold u-width--min-width_100">
                    {t("dimension")}:
                  </span>
                  <span>
                    {color.dimension ? `${color.dimension} ${t("cm")}` : "-"}
                  </span>
                </div>
                <div className="o-row o-row--gap-size_200">
                  <span className="t-bold_semi-bold u-width--min-width_100">
                    {t("paperThickness")}:
                  </span>
                  <span>
                    {color.paperThickness
                      ? `${color.paperThickness} ${t("gram")}`
                      : "-"}
                  </span>
                </div>
                <div className="o-row o-row--gap-size_200">
                  <span className="t-bold_semi-bold u-width--min-width_100">
                    {t("amountOfRolls")}:
                  </span>
                  <span>
                    {color.availableRollsInStock
                      ? `${color.availableRollsInStock} ${t("rollUnit")}`
                      : "-"}
                  </span>
                </div>
                <div className="o-row o-row--gap-size_200">
                  <span className="t-bold_semi-bold u-width--min-width_100">
                    {t("quantity")}:
                  </span>
                  <span>
                    {color.availableStock
                      ? `${color.availableStock} ${t("ton")}`
                      : "-"}
                  </span>
                </div>
                {color.notes && (
                  <div className="o-row o-row--gap-size_200">
                    <span className="t-bold_semi-bold u-width--min-width_100">
                      {t("notes")}:
                    </span>
                    <span>{color.notes}</span>
                  </div>
                )}
              </div>
              <div className="o-row o-row--horizontal-align_end o-row--vertical-align_center">
                <CustomButton
                  variant="blank"
                  icon={editIcon}
                  iconSize={14}
                  title={t("edit")}
                  onClick={() => handleEditRoll(index)}
                  disabled={editingRollIndex !== null}
                />
              </div>
            </div>
          </>
        }
      />
    );
  };

  // Render roll in edit mode (as a Card with inputs)
  const renderEditMode = (color, index) => {
    const data =
      editingRollIndex === index && tempRollData ? tempRollData : color;

    return (
      <CollapsibleCard
        title={`${color.colorCode || t("roll") + (index + 1)}`}
        collapsed={false}
        body={
          <>
            <div className="o-column o-column--gap-size_400">
              <div className="o-row u-width_full-width o-row--gap-size_400">
                <CustomInput
                  type="text"
                  title={t("categoryName")}
                  placeholder={t("categoryName")}
                  value={data.colorCode}
                  onChange={(e) =>
                    handleColorInputChange(index, "colorCode", e.target.value)
                  }
                  onKeyDown={(e) => handleEnter(e, index)}
                  flex
                  error={errorFields[`colorCode-${index}`]}
                />
                <CustomInput
                  type="text"
                  title={t("rollCode")}
                  placeholder={t("rollCode")}
                  value={data.colorName}
                  onChange={(e) =>
                    handleColorInputChange(index, "colorName", e.target.value)
                  }
                  onKeyDown={(e) => handleEnter(e, index)}
                  flex
                  error={errorFields[`colorName-${index}`]}
                />
              </div>

              <div className="o-row u-width_full-width o-row--gap-size_400">
                <CustomInputWithUnit
                  title={t("dimension")}
                  placeholder={t("dimension")}
                  value={data.dimension}
                  onChange={(e) =>
                    handleColorInputChange(index, "dimension", e.target.value)
                  }
                  onKeyDown={(e) => handleEnter(e, index)}
                  unit={t("cm")}
                  flex
                />
                <CustomInputWithUnit
                  title={t("paperThickness")}
                  placeholder={t("paperThickness")}
                  value={data.paperThickness}
                  onChange={(e) =>
                    handleColorInputChange(
                      index,
                      "paperThickness",
                      e.target.value
                    )
                  }
                  onKeyDown={(e) => handleEnter(e, index)}
                  unit={t("gram")}
                  flex
                />
              </div>

              <div className="o-row u-width_full-width o-row--gap-size_400">
                <CustomInputWithUnit
                  title={t("amountOfRolls")}
                  placeholder={t("amountOfRolls")}
                  value={data.availableRollsInStock}
                  onChange={(e) =>
                    handleColorInputChange(
                      index,
                      "availableRollsInStock",
                      e.target.value
                    )
                  }
                  onKeyDown={(e) => handleEnter(e, index)}
                  flex
                  unit={t("rollUnit")}
                />
                <CustomInputWithUnit
                  title={t("quantity")}
                  placeholder={t("quantity")}
                  value={data.availableStock}
                  onChange={(e) =>
                    handleColorInputChange(
                      index,
                      "availableStock",
                      e.target.value
                    )
                  }
                  onKeyDown={(e) => handleEnter(e, index)}
                  unit={t("ton")}
                  flex
                />
              </div>

              <div className="o-row u-width_full-width o-row--gap-size_400">
                <CustomInput
                  type="textarea"
                  title={t("notes")}
                  placeholder={t("notes")}
                  value={data.notes || ""}
                  onChange={(e) =>
                    handleColorInputChange(index, "notes", e.target.value)
                  }
                  flex
                />
              </div>

              {updateLoader && <Loader />}

              {color.isNew && (
                <div className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
                  <CustomButton
                    title={t("delete")}
                    onClick={() => deleteRoll(index)}
                    variant="red"
                    icon={deleteIcon}
                    iconSize={16}
                  />
                </div>
              )}

              {editingRollIndex === index && (
                <div className="o-row o-row--gap-size_300 o-row--horizontal-align_end">
                  <CustomButton
                    title={t("delete")}
                    onClick={() => setShowSureModal(true)}
                    variant="red"
                    icon={deleteIcon}
                    iconSize={16}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancelEdit}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />

                  <CustomButton
                    title={t("save")}
                    onClick={() => handleSaveRoll(index)}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={updateLoader}
                  />
                </div>
              )}

              {showSureModal && (
                <AreYouSureModal
                  onClose={() => setShowSureModal(false)}
                  onConfirm={() => deleteRoll(index)}
                  customMessage={t("areYouSureYouWantToDeleteThisRoll")}
                  closeTitle="cancel"
                  confirmTitle="confirm"
                  confirmIconType="whiteCheck"

                  cancel
                  loading={deleteLoader}
                />
              )}
            </div>
          </>
        }
      />
    );
  };

  return (
    <>
      <div className="c-form-section">
        <label>{t("categoryCode")}</label>
        <CustomInput
          type="text"
          value={currentGroupCode}
          onChange={(e) => setCurrentGroupCode(e.target.value)}
          placeholder={t("example") + "  ER-2280"}
          error={errorFields["code"]}
        />
      </div>

      <div className="c-form-section">
        <label>{t("design")}</label>
        <CustomInput
          type="text"
          value={currentGroupDesign}
          onChange={(e) => setCurrentGroupDesign(e.target.value)}
          placeholder={t("example") + " " + t("striped")}
        />
      </div>

      <div className="o-column o-column--gap-size_300 u-margin--top_300">
        <h3 className="u-margin_0">{t("papersDetails")}</h3>

        {errorFields["colors"] && (
          <>
            <InformationBox variant="error">
              {errorFields["colors"]}
            </InformationBox>
          </>
        )}

        {colorInputs.map((color, index) => (
          <div key={index}>
            {isEditMode && !color.isNew && editingRollIndex !== index
              ? // Show view mode for existing rolls in edit mode
                renderViewMode(color, index)
              : // Show edit mode for new rolls or when editing a specific roll
              !isEditMode || color.isNew || editingRollIndex === index
              ? renderEditMode(color, index)
              : null}
          </div>
        ))}

        {newColorsCount >= 5 && (
          <InformationBox>
            <p>
              {t("cantAddMoreThan5ColorsAtOnce")},{t("saveThenAddAgain")}.
            </p>
          </InformationBox>
        )}

        <div className="o-row o-row--horizontal-align_end u-margin--top_500">
          <CustomButton
            variant={"blank"}
            onClick={handleAddColorField}
            title={t("addAnotherRoll")}
            icon={addIcon}
            iconSize={16}
            disabled={newColorsCount >= 5 || editingRollIndex !== null}
          />
        </div>
      </div>
    </>
  );
};

export default PaperDataEntryModal;
